import { useEffect, useState } from 'react';
import BaseApi from '../../../services/BaseApi';
import Modal from '../../Modal';

export function submissionId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const evidenceFields = [
  ['FOTO_PADRAO_ENTRADA', 'Foto do padrão de entrada', 'Mostre o padrão inteiro. Prefira um enquadramento amplo, que permita identificar o local.'],
  ['FOTO_PADRAO_DISJUNTOR', 'Foto do padrão de entrada com disjuntor', 'Mostre o disjuntor e sua corrente nominal legível.'],
  ['FOTO_FACHADA_PADRAO', 'Foto da fachada com padrão de entrada', 'A fachada e o padrão devem aparecer na mesma foto.'],
  ['FOTO_QUADRO_INTERNO', 'Foto do quadro de disjuntores interno', 'Mostre o quadro e os disjuntores, quando existir.'],
  ['FOTO_LOCAL_INVERSOR', 'Foto do local de instalação do inversor', 'Use uma foto ampla. Prefira um local coberto e próximo ao padrão.'],
  ['VIDEO_QUADRO_INVERSOR', 'Vídeo do quadro geral até o inversor', 'Grave caminhando por todo o percurso entre o quadro e o local do inversor.'],
  ['VIDEO_PADRAO_INVERSOR', 'Vídeo do padrão de entrada até o inversor', 'Grave outro vídeo, mostrando todo o percurso entre o padrão e o local do inversor.'],
  ['FOTO_TELHADO', 'Foto do telhado', 'Fotografe o telhado visto de baixo, sem subir nele.']
];

export function ViabilityChoice({ proposalId, onConfirm }: { proposalId: string; onConfirm: (type: string, id: string) => Promise<void> }) {
  const [options, setOptions] = useState<any>();
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(submissionId);
  useEffect(() => {
    BaseApi.get(`api/proposal/${proposalId}/viability/options`).then(setOptions).catch(e => setError(Modal.getErrorMessageText(e)));
  }, [proposalId]);
  async function confirm() {
    setBusy(true); setError('');
    try { await onConfirm(type, id); }
    catch (e) { setError(Modal.getErrorMessageText(e)); setBusy(false); }
  }
  return <div>
    <p>Escolha o tipo de viabilidade para continuar.</p>
    {options && <>
      <p>Potência total dos inversores: <strong>{options.inverterPowerKw} kW</strong></p>
      <label className="d-block border rounded p-3 mb-2">
        <input type="radio" name="viabilityType" value="RAPIDA" checked={type === 'RAPIDA'} disabled={busy || !options.quickAvailable} onChange={() => { setType('RAPIDA'); setId(submissionId()); }} />{' '}
        <strong>Viabilidade Rápida — R$ 250,00</strong><br />Até 10 kW de inversor. Requer envio dos dados do local antes do pagamento do sinal.
      </label>
      <label className="d-block border rounded p-3 mb-3">
        <input type="radio" name="viabilityType" value="PERSONALIZADA" checked={type === 'PERSONALIZADA'} disabled={busy} onChange={() => { setType('PERSONALIZADA'); setId(submissionId()); }} />{' '}
        <strong>Viabilidade Personalizada/Customizada — R$ 750,00</strong><br />Segue diretamente para o pagamento do sinal.
      </label>
    </>}
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    <button type="button" className="btn btn-primary" disabled={!type || busy} onClick={confirm}>{busy ? 'Confirmando...' : 'Confirmar e continuar'}</button>
  </div>;
}

export function LocalDataForm({ proposalId }: { proposalId: string }) {
  const [study, setStudy] = useState<any>();
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [roof, setRoof] = useState('');
  const [panel, setPanel] = useState('');
  const [files, setFiles] = useState<Record<string, File>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(submissionId);
  useEffect(() => {
    BaseApi.get(`api/proposal/${proposalId}/viability`).then((value: any) => {
      setStudy(value);
      setLatitude(value.location?.latitude?.toString() ?? '');
      setLongitude(value.location?.longitude?.toString() ?? '');
      setRoof(value.roofStructure ?? '');
      setPanel(value.hasInternalBreakerPanel === true ? 'yes' : value.hasInternalBreakerPanel === false ? 'no' : '');
    }).catch(e => setError(Modal.getErrorMessageText(e)));
  }, [proposalId]);
  function changed() { setId(submissionId()); }
  function locate() {
    if (!navigator.geolocation) { setError('Localização indisponível. Informe as coordenadas manualmente.'); return; }
    navigator.geolocation.getCurrentPosition(position => {
      setLatitude(String(position.coords.latitude)); setLongitude(String(position.coords.longitude)); changed(); setError('');
    }, () => setError('Não foi possível obter a localização. Informe as coordenadas manualmente.'), { enableHighAccuracy: true });
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError('');
    const data = new FormData();
    data.append('submissionId', id); data.append('viabilityStudyId', study.viabilityStudyId);
    data.append('latitude', latitude); data.append('longitude', longitude);
    data.append('roofStructure', roof); data.append('hasInternalBreakerPanel', String(panel === 'yes'));
    for (const [category] of study.collectionStatus === "ENVIADA" ? [] : evidenceFields) {
      if (panel === 'no' && (category === 'FOTO_QUADRO_INTERNO' || category === 'VIDEO_QUADRO_INVERSOR')) continue;
      if (files[category]) data.append(category, files[category]);
      else if (!study.evidences?.some((e: any) => e.category === category)) { setError('Envie todos os arquivos obrigatórios.'); return; }
    }
    setBusy(true);
    try { await BaseApi.post(`api/proposal/${proposalId}/viability`, data); window.location.reload(); }
    catch (e) { setError(Modal.getErrorMessageText(e)); setBusy(false); }
  }
  if (study?.collectionStatus === 'ENVIADA') return <div>
    <p>Os dados do local já foram recebidos. Continue para o pagamento do sinal.</p>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Continuando...' : 'Continuar para pagamento do sinal'}</button>
  </div>;
  return <form onSubmit={submit}>
    <p>Viabilidade Rápida: <strong>R$ 250,00</strong>. Envie os dados do local para continuar ao pagamento do sinal.</p>
    <p><a href={`${process.env.PUBLIC_URL || ''}/manual-viabilidade.pdf`} target="_blank" rel="noopener noreferrer">Ver manual com exemplos de fotos e vídeos</a></p>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {!study ? <p>Carregando estudo...</p> : <fieldset disabled={busy || !study.canEdit}>
      {!study.canEdit && <div role="alert" className="alert alert-warning">{study.blockReason || 'Este estudo não permite alterações.'}</div>}
      <p>Use a localização fixa de onde a usina será instalada. Utilize a localização do dispositivo somente se estiver no local.</p>
      <button type="button" className="btn btn-outline-primary mb-2" onClick={locate}>Usar minha localização atual</button>
      <label className="d-block">Latitude<input className="form-control" type="number" step="any" min="-90" max="90" required value={latitude} onChange={e => { setLatitude(e.target.value); changed(); }} /></label>
      <label className="d-block mb-3">Longitude<input className="form-control" type="number" step="any" min="-180" max="180" required value={longitude} onChange={e => { setLongitude(e.target.value); changed(); }} /></label>
      <label className="d-block mb-3">Estrutura do telhado<select className="form-select" required value={roof} onChange={e => { setRoof(e.target.value); changed(); }}>
        <option value="">Selecione</option><option value="MADEIRA">Madeira</option><option value="METALICO">Metálica</option><option value="CONCRETO">Concreto</option>
      </select></label>
      <label className="d-block mb-3">Existe quadro de disjuntores interno?<select className="form-select" required value={panel} onChange={e => { setPanel(e.target.value); changed(); }}>
        <option value="">Selecione</option><option value="yes">Sim</option><option value="no">Não</option>
      </select></label>
      {evidenceFields.filter(([category]) => panel !== 'no' || (category !== 'FOTO_QUADRO_INTERNO' && category !== 'VIDEO_QUADRO_INVERSOR')).map(([category, label, help]) => {
        const existing = study.evidences?.find((e: any) => e.category === category);
        const video = category.startsWith('VIDEO_');
        return <label key={category} className="d-block border rounded p-3 mb-2">
          <strong>{label}</strong><small className="d-block mb-2">{help}</small>
          {existing && <span className="d-block text-success">Arquivo já enviado. Selecione outro para substituir.</span>}
          <input type="file" className="form-control" required={!existing} accept={video ? '.mp4,.mov' : '.jpg,.jpeg,.png,.webp'} onChange={e => {
            const file = e.target.files?.[0];
            if (file && (file.size === 0 || file.size > (video ? 100 : 10) * 1024 * 1024)) {
              setError(`Selecione um arquivo de até ${video ? 100 : 10} MB.`); e.target.value = ''; return;
            }
            setFiles(previous => { const next = { ...previous }; if (file) next[category] = file; else delete next[category]; return next; }); changed();
          }} />
          <small>{video ? 'MP4 ou MOV, até 100 MB.' : 'JPG, PNG ou WebP, até 10 MB.'}</small>
        </label>;
      })}
      <button className="btn btn-primary mt-3" type="submit">{busy ? 'Enviando arquivos...' : 'Concluir dados e seguir para pagamento do sinal'}</button>
    </fieldset>}
  </form>;
}

export default function LocalDataAction({ proposal }: { proposal: any }) {
  function open() {
    Modal.show({ title: 'Dados do Local', body: <LocalDataForm proposalId={proposal.id} />,
      showCancel: true, showConfirm: false, modalProps: { size: 'lg' } });
  }
  useEffect(() => {
    Modal.show({ title: 'Dados do Local', body: <LocalDataForm proposalId={proposal.id} />,
      showCancel: true, showConfirm: false, modalProps: { size: 'lg' } });
  }, [proposal.id]);
  return <button type="button" className="btn btn-primary" onClick={open}>Enviar dados do local</button>;
}
